<template>

  <div class="zeugnisgenerator">
    <!-- Steps -->
    <div class="steps">
      <div></div>
      <a :href="SITEURL" class="flex items-center justify-center">
        <img :src="`${SITEURL}assets/zeugnisgenerator.png`" class="w-20">
      </a>
    </div>

    <!-- Seiteninhalt -->
    <div class="seiteninhalt">
      <div class="zeugnis-uebersicht">
        <div class="grid grid-cols-2 gap-6 mb-16">
          <div class="flex justify-between">
            <h1 class="text-f2 text-primary font-extrabold">
              Statistik
            </h1>

            <div class="flex items-center space-x-4">
              <label for="inactiveSince" class="text-sm">Inaktiv seit mindestens:</label>
              <select v-model="inactiveSince" id="inactiveSince" class="form-select text-primary !pr-8" @change="filterZeugnisse()">
                <option v-for="option in inactiveSinceOptions" :value="option.value">{{ option.text }}</option>
              </select>
            </div>
          </div>

          <div class="flex items-center gap-2 justify-end">
            <div class="border-primary border-solid border bg-white rounded-full w-10 h-10 flex items-center justify-center">
              <i class="fa-duotone fa-thin fa-user text-primary"></i>
            </div>
            <div class="text-f6 text-primary">{{ currentUser.email }}</div>
          </div>
        </div>


        <div class="grid grid-cols-2 gap-6">
          <div v-for="zeugnis in filteredZeugnisse" class="zeugnis flex flex-col gap-3">
            <div class="flex justify-between">
              <div class="text-f3 font-extrabold">{{ zeugnis.vorname }} {{ zeugnis.nachname }}</div>
              <div class="text-f5">Inaktiv seit: {{ formatDate(zeugnis.dateUpdated) }}</div>
            </div>
            <div class="flex justify-between">
              <div class="text-f5 flex gap-4 items-center">
                <div>aktueller Schritt:</div> 
                <div 
                  class="text-primary border-solid border-primary rounded-sm border py-1 px-2 font-medium"
                  :class="[zeugnis.aktuellerSchritt ? 'text-primary border-primary' : 'text-rot border-rot' ]"
                >
                  {{ zeugnis.aktuellerSchritt ?? 'nicht definiert' }}
                </div>
              </div>
              <div class="text-f5 flex gap-4 items-center">
                <div>verantwortlich:</div> 
                <div
                  class="border-solid rounded-sm border py-1 px-2 font-medium"
                  :class="[getVerantwortlichePerson(zeugnis).fullName == 'nicht zugewiesen' ? 'text-rot border-rot' : 'text-primary border-primary' ]"
                >
                  {{ getVerantwortlichePerson(zeugnis).fullName }}
                </div>
              </div>
            </div>
            <div class="flex justify-between mt-5">
              <button @click="sendEmailNotification(zeugnis)" class="btn">Erinnerungsmail auslösen</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation rechts -->
    <div class="navigation">
      <div class="flex flex-col gap-9">
        <button 
          class="navigation-button"
          tabindex="-1"
          disabled
        >
          <div class="navigation-circle">
            <i class="fa-duotone fa-thin fa-gear"></i>
          </div>
          <div class="navigation-text">Einstellungen</div>
        </button>

        <a href="/prozess-uebersicht" 
          class="navigation-button current-navigation"
          tabindex="-1"
        >
          <div class="navigation-circle">
            <i class="fa-duotone fa-thin fa-chart-simple"></i>
          </div>
          <div class="navigation-text">Statistik</div>
        </a>

        <a href="/information" class="navigation-button" tabindex="-1">
          <div class="navigation-circle">
            <i class="fa-duotone fa-thin fa-circle-info"></i>
          </div>
          <div class="navigation-text">Information</div>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, Ref, onMounted} from "vue";
import {BASEURL, SITEURL} from "@libTs/cms.globals.js";
import axios from "axios";
import { ZeugnisseQuery, UsersQuery } from "../gql/vorlagen";

const {gqlToken, currentUser, generatorUrl} = defineProps({
  gqlToken: {
    type: String,
    required: true
  },
  currentUser: {
    type: Object,
    required: true
  },
  generatorUrl: {
    type: String,
    required: true
  }
});

const graphqlUrl = BASEURL + '/actions/graphql/api';

const users: Ref<any> = ref([]);

const loading = ref(false);
const alerts = ref([]);


const allZeugnisse = ref([]);
const filteredZeugnisse = ref([]);

const inactiveSince = ref(30);

const inactiveSinceOptions = ref([
  {value: 0, text: 'Alle'},
  {value: 1, text: '1 Tag'},
  {value: 7, text: '1 Woche'},
  {value: 30, text: '1 Monat'},
  {value: 90, text: '3 Monate'},
]);


onMounted(() => {
  getZeugnisse();
  getUsers();
});

const getUsers = async () => {
  axios.post(graphqlUrl, {
        query: UsersQuery
      }, {
        headers: {
          'Authorization': "Bearer " + gqlToken
        }
      }
  ).then((response) => {
    users.value = response.data.data.users;
  })
}


const getZeugnisse = () => {
  axios.post(graphqlUrl, {
        query: ZeugnisseQuery,
      }, {
        headers: {
          'Authorization': "Bearer " + gqlToken
        }
      }
  ).then((response) => {
    allZeugnisse.value = response.data.data.zeugnisseEntries;
    filteredZeugnisse.value = [];

    filterZeugnisseInit();

    console.log(allZeugnisse.value);
  }).catch((error) => {
    console.log(error);
  });
}

const filterZeugnisse = () => {
  if (inactiveSince.value === 0) {
    filteredZeugnisse.value = allZeugnisse.value;
  } else {
    let date = new Date();
    date.setDate(date.getDate() - inactiveSince.value);

    filteredZeugnisse.value = allZeugnisse.value.filter((entry: any) => {
      return new Date(entry.dateUpdated) < date;
    });
  }
}

const filterZeugnisseInit = () => {
  inactiveSinceOptions.value.slice().reverse().forEach((inactiveSinceOption: any) => {
    if(filteredZeugnisse.value.length == 0) {
      inactiveSince.value = inactiveSinceOption.value;
      filterZeugnisse();
    }
  })
}

const formatDate = (date: string) => {
  let d = new Date(date);

  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  let year = d.getFullYear();

  return `${day}.${month}.${year}`;
}

const formatDateTime = (date: string) => {
  let d = new Date(date);

  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  let year = d.getFullYear();

  let hour = ("0" + d.getHours()).slice(-2);
  let minute = ("0" + d.getMinutes()).slice(-2);

  return `${day}.${month}.${year} ${hour}:${minute}`;
}

const downloadWord = () => {

  axios.post(generatorUrl + 'docx', {
      vorname: savedEntry.value.vorname,
      nachname: savedEntry.value.nachname,
      text: savedEntry.value.erstelltesZeugnis.replace(/<[^>]*>?/gm, ''),
      geburtsdatum: savedEntry.value.geburtsdatum,
      heimatort: savedEntry.value.heimatort
    }, {
      responseType: 'blob', // important
    }
  ).then((response) => {
      // create file link in browser's memory
      const href = URL.createObjectURL(response.data);

      // create "a" HTML element with href to file & click
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', 'Arbeitszeugnis.docx'); //or any other extension
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      showOverlay.value = true;
  });

}

const downloadPDF = () => {

  axios.post(generatorUrl + 'pdf', {
      vorname: savedEntry.value.vorname,
      nachname: savedEntry.value.nachname,
      text: savedEntry.value.erstelltesZeugnis.replace(/<[^>]*>?/gm, ''),
      geburtsdatum: savedEntry.value.geburtsdatum,
      heimatort: savedEntry.value.heimatort
    }, {
      responseType: 'blob', // important
    }
  ).then((response) => {
      // create file link in browser's memory
      const href = URL.createObjectURL(response.data);

      // create "a" HTML element with href to file & click
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', 'Arbeitszeugnis.pdf'); //or any other extension
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);

      showOverlay.value = true;
  });

}

const getVerantwortlichePerson = (zeugnis) => {

  console.log(zeugnis);

  if(zeugnis.nachrichten.length > 0 && zeugnis.nachrichten[zeugnis.nachrichten.length - 1].zugewiesenePerson.length > 0) {
    return zeugnis.nachrichten[zeugnis.nachrichten.length - 1].zugewiesenePerson[0];
  } else if(zeugnis.hrVerantwortlicher.length > 0) {
    return zeugnis.hrVerantwortlicher[0]
  }
  else {
    return {fullName: 'nicht zugewiesen'};
  }
}

const sendEmailNotification = (zeugnis: any) => {

  if(getVerantwortlichePerson(zeugnis).fullName === 'nicht zugewiesen') {
    return;
  }

  axios.post('/actions/zeugnisgenerator/mail/send', {
    to: getVerantwortlichePerson(zeugnis).email,
    subject: "Erinnerung: Arbeitszeugnis noch nicht bearbeitet - " + zeugnis.vorname + " " + zeugnis.nachname,
    message: "Das Arbeitszeugnis von " + zeugnis.vorname + " " + zeugnis.nachname + " ist seit dem " + formatDate(zeugnis.dateUpdated) + " inaktiv. Bitte überprüfe den Status des Zeugnisses.",
    zeugnis: SITEURL + '/zeugnis/' + zeugnis.uid
  }).then((response) => {
    console.log(response);
  });
}


</script>
